
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { doc, onSnapshot, type Timestamp, updateDoc } from 'firebase/firestore';

export type UserProfile = {
  uid: string;
  username?: string;
  email?: string;
  photoURL?: string;
  country?: string;
  countryMismatch?: boolean;
  mismatchedCountry?: string;
  totalEarnings: number;
  availableBalance: number;
  pendingBalance: number;
  totalWithdrawn: number;
  totalDeducted: number;
  lastActivityAt?: Date;
  createdAt?: Date; // Added for the new user notice
};


interface UserContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Function to get country from a client-side API route
const getCurrentCountry = async (): Promise<string | null> => {
    try {
        // We use a reliable third-party service to get geo-IP info.
        // This runs on the client-side, so it reflects the user's current IP.
        const response = await fetch('https://ipapi.co/country_code');
        if (!response.ok) {
            console.warn(`ipapi.co failed with status: ${response.status}`);
            return null;
        }
        const countryCode = await response.text();
        return countryCode.trim() || null;
    } catch (error) {
        console.warn("Could not fetch current country from ipapi.co:", error);
        return null;
    }
}


export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      setLoading(true);
      if (authUser) {
        setUser(authUser);
        const userDocRef = doc(db, 'users', authUser.uid);
        
        const unsubscribeSnapshot = onSnapshot(userDocRef, async (docSnapshot) => {
          if (docSnapshot.exists()) {
             const data = docSnapshot.data();

             const totalEarningsFromDB = data.totalEarnings || 0;
             const pendingBalanceFromDB = data.pendingBalance || 0;
             const totalWithdrawnFromDB = data.totalWithdrawn || 0;
             const totalDeductedFromDB = data.totalDeducted || 0;
             
             const availableBalance = totalEarningsFromDB - totalWithdrawnFromDB - pendingBalanceFromDB;
             
             const profile: UserProfile = {
                uid: authUser.uid,
                username: data.username || authUser.displayName || 'Anonymous',
                email: data.email || authUser.email || undefined,
                photoURL: data.photoURL || authUser.photoURL || undefined,
                country: data.country || 'XX',
                countryMismatch: data.countryMismatch || false,
                mismatchedCountry: data.mismatchedCountry,
                totalEarnings: totalEarningsFromDB,
                availableBalance: availableBalance,
                pendingBalance: pendingBalanceFromDB,
                totalWithdrawn: totalWithdrawnFromDB,
                totalDeducted: totalDeductedFromDB,
                lastActivityAt: (data.lastActivityAt as Timestamp)?.toDate(),
                createdAt: (data.createdAt as Timestamp)?.toDate(),
             };
             
             setUserProfile(profile);

             // --- Country Mismatch Check ---
             if (profile.country && profile.country !== 'XX' && !profile.countryMismatch) {
                const currentCountry = await getCurrentCountry();
                if (currentCountry && currentCountry !== profile.country) {
                    console.warn(`Country mismatch detected for user ${profile.uid}. Registered: ${profile.country}, Current: ${currentCountry}`);
                    await updateDoc(userDocRef, { countryMismatch: true, mismatchedCountry: currentCountry });
                }
             }


          } else {
             setUserProfile({
                uid: authUser.uid,
                username: authUser.displayName || 'Anonymous',
                email: authUser.email || undefined,
                photoURL: authUser.photoURL || undefined,
                country: 'XX',
                countryMismatch: false,
                totalEarnings: 0,
                availableBalance: 0,
                pendingBalance: 0,
                totalWithdrawn: 0,
                totalDeducted: 0,
                createdAt: authUser.metadata.creationTime ? new Date(authUser.metadata.creationTime) : new Date(),
            });
          }
          setLoading(false);
        }, (error) => {
            console.error("Error fetching user profile:", error);
            setLoading(false);
        });

        return () => unsubscribeSnapshot();
      } else {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  return (
    <UserContext.Provider value={{ user, userProfile, loading }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
